var CoreObject = require("enyo/CoreObject"),
    kind       = require("enyo/kind"),
    logger     = require("enyo/logger");

/**
 * This object is meant to provide a context for executing a particular route.
 */
module.exports = kind(/** @lends app.core.Route.prototype */ {

    /**
     * @private
     */
    name: "app.core.Route",

    /**
     * @private
     */
    kind: CoreObject,

    /**
     * A flag for the router to check if the handler has finished executing.
     *
     * @type {boolean}
     * @public
     */
    finished: false,

    /**
     * Middleware tasks to execute
     *
     * @type {array<array|function>}
     * @public
     */
    middleware: [],

    /**
     * Handle the route by executing middleware first and exeucting the handler
     * afterwards, as long as it execution hasn't finished yet.
     *
     * @param    {app.core.App}  app         The application core
     * @param    {string}        pathname    The matched pathname
     * @param    {Object}        params      The parameters extracted from the
     *                                       pathname
     * @param    {string}        search      The current search string
     * @param    {boolean}       skipCache   The cached data should be ignored
     */
    handle: function(app, pathname, params, search, skipCache) {
        this.executeMiddleware(app, pathname, params, search, skipCache).then(this.bindSafely(function() {
            if (!this.finished) {
                this.execute(app, pathname, params, search, skipCache);
            }
        }));
    },

    /**
     * Execute middleware tasks before executing a handler specific to the
     * route.
     *
     * @param    {app.core.App}  app         The application core
     * @param    {string}        pathname    The matched pathname
     * @param    {Object}        params      The parameters extracted from the
     *                                       pathname
     * @param    {string}        search      The current search string
     * @param    {boolean}       skipCache   The cached data should be ignored
     * @returns  {Promise}       A promise which will resolve when all
     *                           middleware has executed or route handling has
     *                           been marked as finished.
     * @public
     */
    executeMiddleware: function(app, pathname, params, search, skipCache) {
        var that = this,
            middleware,
            next;

        middleware = this.middleware.concat().map(function(set) {
            return Array.isArray(set) ? set : [set];
        });

        next = function() {
            var tasks;

            if (!middleware.length) {
                return Promise.resolve();
            }

            tasks = middleware.shift();

            return Promise.all(tasks.map(function(task) {
                return task(that, app, pathname, params, search, skipCache);
            })).finally(function() {
                if (that.finished) {
                    return Promise.resolve();
                }
                else {
                    return next();
                }
            });
        };

        return next();
    },

    /**
     * Handler for the route
     *
     * @param  {app.core.App}  app         The application core
     * @param  {string}        pathname    The matched pathname
     * @param  {Object}        params      The parameters extracted from the
     *                                     pathname
     * @param  {string}        search      The current search string
     * @param  {boolean}       skipCache   The cached data should be ignored
     * @public
     */
    execute: function() {
        var errorMsg = "app.Route.execute() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * @public
     */
    finish: function() {
        if (!this.finished) {
            this.finished = true;
        }
    },

    /**
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.finish();
            sup.apply(this, arguments);
        };
    })

});
